import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useTranslation } from '@/lib/i18n';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Single permission required to access this route
   */
  permission?: string;
  /**
   * Multiple permissions where at least one is required
   */
  anyPermission?: string[];
  /**
   * Multiple permissions where all are required
   */
  allPermissions?: string[];
  /**
   * Custom redirect path when not authenticated (default: /login)
   */
  redirectTo?: string;
  /**
   * Custom path for 403 forbidden page (default: /403)
   */
  forbiddenPath?: string;
}

// ─────────────────────────────────────────────────────────────
// Loading Component
// ─────────────────────────────────────────────────────────────

function LoadingSpinner() {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Forbidden Page Component
// ─────────────────────────────────────────────────────────────

function ForbiddenPage() {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-destructive">403</h1>
        <h2 className="mt-4 text-2xl font-semibold text-foreground">
          {t('error.forbidden.title')}
        </h2>
        <p className="mt-2 text-muted-foreground">
          {t('error.forbidden.description')}
        </p>
        <a
          href="/"
          className="mt-6 inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t('common.back')}
        </a>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Service Unavailable Component
// ─────────────────────────────────────────────────────────────

/**
 * Shown when the session could not be rehydrated because the SERVER was out
 * of reach (429, 5xx, network drop) — never because it expired.
 *
 * Sending the user to /login here would state an expiry that did not
 * happen: the tokens are still in storage and still valid as far as anyone
 * knows. Showing nothing at all would leave a blank page with no reason.
 */
function ServiceUnavailablePage({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-semibold text-foreground">
          {t('auth.unavailable.title')}
        </h1>
        <p className="mt-3 text-muted-foreground">
          {t('auth.unavailable.description')}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-6 inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t('common.retry')}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Protected Route Component
// ─────────────────────────────────────────────────────────────

export function ProtectedRoute({
  children,
  permission,
  anyPermission,
  allPermissions,
  redirectTo = '/login',
  forbiddenPath,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, sessionError, retrySession } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Unreachable server ≠ expired session: say so, and offer to try again.
  if (!isAuthenticated && sessionError === 'unavailable') {
    return <ServiceUnavailablePage onRetry={retrySession} />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the current location to redirect back after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check permissions if specified
  let hasRequiredPermissions = true;

  if (permission) {
    hasRequiredPermissions = hasPermission(permission);
  }

  if (anyPermission && anyPermission.length > 0) {
    hasRequiredPermissions = hasRequiredPermissions && hasAnyPermission(...anyPermission);
  }

  if (allPermissions && allPermissions.length > 0) {
    hasRequiredPermissions = hasRequiredPermissions && hasAllPermissions(...allPermissions);
  }

  // Show 403 page if permission denied
  if (!hasRequiredPermissions) {
    if (forbiddenPath) {
      return <Navigate to={forbiddenPath} replace />;
    }
    return <ForbiddenPage />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
